import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const HodReviewRequests = ({ onNavigate, userInfo }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Review requests data
  const [reviewRequests, setReviewRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [semesterFilter, setSemesterFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Action states
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [selectedRequests, setSelectedRequests] = useState(new Set());

  // Statistics
  const [statistics, setStatistics] = useState({
    totalRequests: 0,
    pendingRequests: 0,
    inReviewRequests: 0,
    completedRequests: 0,
    rejectedRequests: 0,
    todaySubmissions: 0
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);





  // Fetch data from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Starting API call to fetch review requests data...');

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
          setError('Authentication token not found. Please login again.');
          return;
        }



        // Fetch review requests data - NEW ENDPOINT
        console.log('📊 Fetching review requests data...');
        const requestsResponse = await fetch(`${API_LINK}/api/hod/review-requests`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!requestsResponse.ok) {
          throw new Error(`HTTP error! status: ${requestsResponse.status}`);
        }

        const requestsResult = await requestsResponse.json();

        if (requestsResult.success) {
          console.log('✅ Review requests data loaded successfully');
          console.log(`📈 Found ${requestsResult.data.length} review requests`);

          setReviewRequests(requestsResult.data);
          setFilteredRequests(requestsResult.data);

          // Calculate statistics
          const stats = {
            totalRequests: requestsResult.data.length,
            pendingRequests: requestsResult.data.filter(r => r.status === 'pending').length,
            inReviewRequests: requestsResult.data.filter(r => r.status === 'in-review').length,
            completedRequests: requestsResult.data.filter(r => r.status === 'completed').length,
            rejectedRequests: requestsResult.data.filter(r => r.status === 'rejected').length,
            todaySubmissions: requestsResult.data.filter(r => {
              const today = new Date().toDateString();
              const requestDate = new Date(r.created_at).toDateString();
              return today === requestDate;
            }).length
          };

          setStatistics(stats);
        } else {
          throw new Error(requestsResult.message || 'Failed to fetch review requests');
        }

      } catch (err) {
        console.error('💥 Error loading review requests data:', err);
        setError(`Failed to load data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userInfo]);

  // Filter and sort requests
  useEffect(() => {
    let filtered = [...reviewRequests];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }

    // Apply semester filter
    if (semesterFilter !== 'all') {
      filtered = filtered.filter(request => request.semester === semesterFilter);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(request =>
        request.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.student_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.semester.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.academic_year.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.subject_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.reason.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'student':
          return a.student_name.localeCompare(b.student_name);
        case 'semester':
          return a.semester.localeCompare(b.semester);
        case 'status':
          return a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });

    setFilteredRequests(filtered);
    setCurrentPage(1);
  }, [reviewRequests, statusFilter, searchTerm, semesterFilter, sortBy]);





  // Handle review action (approve/reject/update status)
  const handleReviewAction = async (requestId, newStatus, notes = '', reviewerName = 'HOD') => {
    try {
      setActionLoading(true);
      setActionError(null);
      setActionSuccess(null);

      console.log(`🔄 Processing ${newStatus} for request ${requestId}`);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(`${API_LINK}/api/hod/review-requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          notes: notes
        })
      });

      const result = await response.json();

      if (result.success) {
        console.log(`✅ Successfully ${newStatus} request ${requestId}`);

        // Update local state
        setReviewRequests(prev => prev.map(request =>
          request.id === requestId ? {
            ...request,
            status: newStatus,
            review_notes: notes,
            reviewed_by: reviewerName,
            reviewed_at: new Date().toISOString()
          } : request
        ));

        if (selectedRequest && selectedRequest.id === requestId) {
          setSelectedRequest({
            ...selectedRequest,
            status: newStatus,
            review_notes: notes,
            reviewed_by: reviewerName,
            reviewed_at: new Date().toISOString()
          });
        }

        setActionSuccess(result.message);
        setReviewNotes('');

        setTimeout(() => setActionSuccess(null), 3000);
      } else {
        setActionError(result.message);
      }

    } catch (error) {
      console.error('💥 Error updating review request:', error);
      setActionError(`Failed to perform action: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle bulk actions
  const handleBulkAction = async (action) => {
    if (selectedRequests.size === 0) {
      setActionError('Please select requests to perform bulk action');
      return;
    }

    const confirmMessage = `Are you sure you want to ${action} ${selectedRequests.size} selected requests?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    console.log(`🔄 Performing bulk ${action} on ${selectedRequests.size} requests`);

    for (const requestId of selectedRequests) {
      await handleReviewAction(requestId, action);
    }

    setSelectedRequests(new Set());
  };

  // Toggle request selection
  const toggleRequestSelection = (requestId) => {
    const newSelection = new Set(selectedRequests);
    if (newSelection.has(requestId)) {
      newSelection.delete(requestId);
    } else {
      newSelection.add(requestId);
    }
    setSelectedRequests(newSelection);
  };

  // Select/deselect all requests
  const toggleAllSelection = () => {
    if (selectedRequests.size === currentRequests.length) {
      setSelectedRequests(new Set());
    } else {
      setSelectedRequests(new Set(currentRequests.map(request => request.id)));
    }
  };



  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status info
  const getStatusInfo = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return { color: '#ffc107', bg: '#fff3cd', icon: '⏳', text: 'Pending Review' };
      case 'in-review':
        return { color: '#17a2b8', bg: '#d1ecf1', icon: '🔍', text: 'In Review' };
      case 'completed':
        return { color: '#28a745', bg: '#d4edda', icon: '✅', text: 'Completed' };
      case 'rejected':
        return { color: '#dc3545', bg: '#f8d7da', icon: '❌', text: 'Rejected' };
      default:
        return { color: '#6c757d', bg: '#e2e3e5', icon: '❓', text: status };
    }
  };

  // Get unique semesters for filter
  const getUniqueSemesters = () => {
    const semesters = [...new Set(reviewRequests.map(request => request.semester))];
    return semesters.sort();
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRequests = filteredRequests.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);

  return (
    <Layout role="hod" onNavigate={onNavigate} userInfo={userInfo} activePage="hod-review-requests">
      {({ hodData, isMobile, handleLogout }) => (
        <>
          {/* Loading Component */}
          {loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 'calc(100vh - 200px)',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid #e3f2fd',
                borderTop: '4px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Review Requests...</p>
            </div>
          )}

          {/* Error Component */}
          {error && !loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 'calc(100vh - 200px)',
              flexDirection: 'column',
              gap: '1rem',
              padding: '2rem'
            }}>
              <div style={{
                backgroundColor: '#ffffff',
                padding: '3rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                textAlign: 'center',
                maxWidth: '400px',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{ fontSize: '4rem', color: '#ff6b6b', marginBottom: '1.5rem' }}>⚠️</div>
                <h3 style={{ color: '#333', margin: '0 0 1rem 0', fontSize: '1.4rem', fontWeight: 600 }}>Unable to Load Review Requests</h3>
                <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem', lineHeight: 1.5 }}>{error}</p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => window.location.reload()} style={{ backgroundColor: '#238548', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 500 }}>Try Again</button>
                  <button onClick={handleLogout} style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 500 }}>Logout</button>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          {!loading && !error && (
            <>
              {/* Modal CSS */}
              <style>
                {`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 2000;
            padding: 1rem;
            box-sizing: border-box;
          }
          
          .modal-content {
            background: white;
            border-radius: 12px;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
            max-width: 900px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;
            position: relative;
          }
        `}
              </style>
              {/* Page Header */}
              <div style={{
                background: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem',
                borderLeft: '4px solid #238548'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '1rem'
                }}>
                  <div>
                    <h2 style={{
                      color: '#238548',
                      margin: '0 0 0.5rem 0',
                      fontSize: '1.8rem',
                      fontWeight: 'bold'
                    }}>
                      📋 Marks Review Requests
                    </h2>
                    <p style={{
                      color: '#666',
                      margin: 0,
                      fontSize: '1rem'
                    }}>
                      Review and manage student marks review requests for {hodData.department}
                    </p>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{
                      background: '#f8f9fa',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      <strong>Total: {statistics.totalRequests}</strong>
                    </div>

                    <div style={{
                      background: '#fff3cd',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      border: '1px solid #ffeaa7'
                    }}>
                      Pending: {statistics.pendingRequests}
                    </div>
                  </div>
                </div>
              </div>

              {/* Statistics Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem'
              }}>
                {[
                  {
                    icon: '📋',
                    title: 'Total Requests',
                    value: statistics.totalRequests,
                    subtitle: 'All time requests',
                    color: '#238548'
                  },
                  {
                    icon: '⏳',
                    title: 'Pending Review',
                    value: statistics.pendingRequests,
                    subtitle: 'Awaiting your review',
                    color: '#ffc107'
                  },
                  {
                    icon: '🔍',
                    title: 'In Review',
                    value: statistics.inReviewRequests,
                    subtitle: 'Currently reviewing',
                    color: '#17a2b8'
                  },
                  {
                    icon: '✅',
                    title: 'Completed',
                    value: statistics.completedRequests,
                    subtitle: 'Successfully reviewed',
                    color: '#28a745'
                  },
                  {
                    icon: '❌',
                    title: 'Rejected',
                    value: statistics.rejectedRequests,
                    subtitle: 'Rejected requests',
                    color: '#dc3545'
                  },
                  {
                    icon: '📅',
                    title: 'Today',
                    value: statistics.todaySubmissions,
                    subtitle: 'Submitted today',
                    color: '#6f42c1'
                  }
                ].map((stat, index) => (
                  <div key={index} style={{
                    background: '#ffffff',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                    }}>
                    <div style={{
                      fontSize: '2rem',
                      color: stat.color,
                      marginBottom: '0.5rem'
                    }}>
                      {stat.icon}
                    </div>
                    <h3 style={{ margin: '0 0 0.25rem 0', color: '#333', fontSize: '0.9rem' }}>{stat.title}</h3>
                    <p style={{
                      fontSize: '1.8rem',
                      fontWeight: 'bold',
                      color: stat.color,
                      margin: '0.25rem 0'
                    }}>
                      {stat.value}
                    </p>
                    <p style={{
                      color: '#666',
                      margin: 0,
                      fontSize: '0.8rem'
                    }}>
                      {stat.subtitle}
                    </p>
                  </div>
                ))}
              </div>

              {/* Action Messages */}
              {(actionSuccess || actionError) && (
                <div style={{
                  background: actionSuccess ? '#d4edda' : '#f8d7da',
                  border: `1px solid ${actionSuccess ? '#c3e6cb' : '#f5c6cb'}`,
                  color: actionSuccess ? '#155724' : '#721c24',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>
                    {actionSuccess ? '✅' : '❌'}
                  </span>
                  {actionSuccess || actionError}
                </div>
              )}

              {/* Filters and Search */}
              <div style={{
                background: '#ffffff',
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  alignItems: 'end'
                }}>
                  {/* Search */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      🔍 Search
                    </label>
                    <input
                      type="text"
                      placeholder="Search by student, semester, subject..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e9ecef';
                      }}
                    />
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Status Filter
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="in-review">In Review</option>
                      <option value="completed">Completed</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>

                  {/* Semester Filter */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Semester Filter
                    </label>
                    <select
                      value={semesterFilter}
                      onChange={(e) => setSemesterFilter(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="all">All Semesters</option>
                      {getUniqueSemesters().map(semester => (
                        <option key={semester} value={semester}>{semester}</option>
                      ))}
                    </select>
                  </div>

                  {/* Sort By */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Sort By
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="student">Student Name</option>
                      <option value="semester">Semester</option>
                      <option value="status">Status</option>
                    </select>
                  </div>

                  {/* Clear Filters */}
                  <div>
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setStatusFilter('all');
                        setSemesterFilter('all');
                        setSortBy('newest');
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: '#f8f9fa',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        color: '#333',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#e9ecef';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f8f9fa';
                      }}
                    >
                      🗑️ Clear Filters
                    </button>
                  </div>
                </div>
              </div>

              {/* Bulk Actions */}
              {selectedRequests.size > 0 && (
                <div style={{
                  background: '#e3f2fd',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '2rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: '1px solid #bbdefb'
                }}>
                  <span style={{ fontWeight: '500', color: '#1976d2' }}>
                    {selectedRequests.size} request(s) selected
                  </span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleBulkAction('in-review')}
                      disabled={actionLoading}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#17a2b8',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '500'
                      }}
                    >
                      🔍 Mark In Review
                    </button>
                    <button
                      onClick={() => handleBulkAction('completed')}
                      disabled={actionLoading}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#28a745',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '500'
                      }}
                    >
                      ✅ Complete All
                    </button>
                    <button
                      onClick={() => handleBulkAction('rejected')}
                      disabled={actionLoading}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#dc3545',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '500'
                      }}
                    >
                      ❌ Reject All
                    </button>
                  </div>
                </div>
              )}

              {/* Review Requests List */}
              <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                marginBottom: '2rem'
              }}>
                {/* Table Header */}
                <div style={{
                  background: '#f8f9fa',
                  padding: '1rem',
                  borderBottom: '1px solid #e9ecef',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <input
                    type="checkbox"
                    checked={selectedRequests.size === currentRequests.length && currentRequests.length > 0}
                    onChange={toggleAllSelection}
                    style={{
                      width: '16px',
                      height: '16px',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ fontWeight: '500', color: '#333' }}>
                    Select All ({currentRequests.length} requests)
                  </span>
                </div>

                {currentRequests.length > 0 ? (
                  currentRequests.map((request, index) => {
                    const statusInfo = getStatusInfo(request.status);

                    return (
                      <div
                        key={request.id}
                        style={{
                          padding: '1.5rem',
                          borderBottom: index < currentRequests.length - 1 ? '1px solid #ecf0f1' : 'none',
                          transition: 'all 0.3s ease',
                          backgroundColor: '#ffffff'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#ffffff';
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          gap: '1rem',
                          alignItems: 'start'
                        }}>
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={selectedRequests.has(request.id)}
                            onChange={() => toggleRequestSelection(request.id)}
                            style={{
                              width: '16px',
                              height: '16px',
                              cursor: 'pointer',
                              marginTop: '0.25rem'
                            }}
                          />

                          {/* Request Content */}
                          <div style={{ flex: 1 }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'start',
                              marginBottom: '1rem',
                              flexWrap: 'wrap',
                              gap: '1rem'
                            }}>
                              <div style={{ flex: 1, minWidth: '300px' }}>
                                <h3 style={{
                                  margin: '0 0 0.5rem 0',
                                  fontSize: '1.1rem',
                                  fontWeight: 'bold',
                                  color: '#333'
                                }}>
                                  {request.student_name} ({request.student_id})
                                </h3>
                                <p style={{
                                  margin: '0 0 0.5rem 0',
                                  color: '#666',
                                  fontSize: '0.9rem'
                                }}>
                                  <strong>{request.semester}</strong> • {request.academic_year}
                                  {request.subject_code && ` • Subject: ${request.subject_code}`}
                                </p>
                                <p style={{
                                  margin: '0 0 0.5rem 0',
                                  color: '#777',
                                  fontSize: '0.85rem'
                                }}>
                                  📝 {request.reason.length > 100 ? `${request.reason.substring(0, 100)}...` : request.reason}
                                </p>
                              </div>

                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: '0.5rem',
                                minWidth: '150px'
                              }}>
                                <span style={{
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '15px',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  backgroundColor: statusInfo.bg,
                                  color: statusInfo.color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }}>
                                  {statusInfo.icon} {statusInfo.text}
                                </span>

                                <div style={{
                                  fontSize: '0.8rem',
                                  color: '#999',
                                  textAlign: 'right'
                                }}>
                                  <div>📅 {formatDate(request.created_at)}</div>
                                  <div>🕒 {formatTime(request.created_at)}</div>
                                </div>
                              </div>
                            </div>

                            {/* Review Notes */}
                            {request.review_notes && (
                              <div style={{
                                background: '#f8f9fa',
                                padding: '0.75rem',
                                borderRadius: '6px',
                                marginBottom: '1rem',
                                border: '1px solid #e9ecef'
                              }}>
                                <div style={{
                                  fontSize: '0.8rem',
                                  color: '#666',
                                  marginBottom: '0.25rem'
                                }}>
                                  Review by {request.reviewed_by} • {formatDate(request.reviewed_at)}
                                </div>
                                <div style={{ fontSize: '0.9rem', color: '#333' }}>
                                  {request.review_notes}
                                </div>
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div style={{
                              display: 'flex',
                              gap: '0.5rem',
                              alignItems: 'center',
                              flexWrap: 'wrap'
                            }}>
                              <button
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setShowDetailModal(true);
                                  setActionError(null);
                                  setActionSuccess(null);
                                }}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: '#17a2b8',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  fontWeight: '500'
                                }}
                              >
                                👁️ View Details
                              </button>

                              {request.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleReviewAction(request.id, 'in-review', '', hodData.name || 'HOD')}
                                    disabled={actionLoading}
                                    style={{
                                      padding: '0.5rem 1rem',
                                      background: '#17a2b8',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem',
                                      fontWeight: '500',
                                      opacity: actionLoading ? 0.7 : 1
                                    }}
                                  >
                                    🔍 Start Review
                                  </button>

                                  <button
                                    onClick={() => handleReviewAction(request.id, 'completed')}
                                    disabled={actionLoading}
                                    style={{
                                      padding: '0.5rem 1rem',
                                      background: '#28a745',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem',
                                      fontWeight: '500',
                                      opacity: actionLoading ? 0.7 : 1
                                    }}
                                  >
                                    ✅ Complete
                                  </button>

                                  <button
                                    onClick={() => handleReviewAction(request.id, 'rejected')}
                                    disabled={actionLoading}
                                    style={{
                                      padding: '0.5rem 1rem',
                                      background: '#dc3545',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem',
                                      fontWeight: '500',
                                      opacity: actionLoading ? 0.7 : 1
                                    }}
                                  >
                                    ❌ Reject
                                  </button>
                                </>
                              )}

                              {request.status === 'in-review' && (
                                <>
                                  <button
                                    onClick={() => handleReviewAction(request.id, 'completed')}
                                    disabled={actionLoading}
                                    style={{
                                      padding: '0.5rem 1rem',
                                      background: '#28a745',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem',
                                      fontWeight: '500',
                                      opacity: actionLoading ? 0.7 : 1
                                    }}
                                  >
                                    ✅ Complete Review
                                  </button>

                                  <button
                                    onClick={() => handleReviewAction(request.id, 'rejected')}
                                    disabled={actionLoading}
                                    style={{
                                      padding: '0.5rem 1rem',
                                      background: '#dc3545',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem',
                                      fontWeight: '500',
                                      opacity: actionLoading ? 0.7 : 1
                                    }}
                                  >
                                    ❌ Reject
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{
                    padding: '3rem',
                    textAlign: 'center',
                    color: '#666'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                    <h3 style={{ margin: '0 0 1rem 0' }}>No Review Requests Found</h3>
                    <p style={{ margin: 0 }}>
                      {searchTerm || statusFilter !== 'all' || semesterFilter !== 'all'
                        ? 'Try adjusting your search filters'
                        : 'No marks review requests have been submitted yet'
                      }
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '2rem'
                }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #238548',
                      borderRadius: '8px',
                      background: currentPage === 1 ? '#f8f9fa' : '#238548',
                      color: currentPage === 1 ? '#6c757d' : '#ffffff',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    ← Previous
                  </button>

                  <span style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.9rem',
                    color: '#666'
                  }}>
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #238548',
                      borderRadius: '8px',
                      background: currentPage === totalPages ? '#f8f9fa' : '#238548',
                      color: currentPage === totalPages ? '#6c757d' : '#ffffff',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Next →
                  </button>
                </div>
              )}


              {/* Detail Modal */}
              {showDetailModal && selectedRequest && (
                <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
                  <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    {/* Modal Header */}
                    <div style={{
                      background: '#238548',
                      color: '#ffffff',
                      padding: '1.5rem 2rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 'bold' }}>
                        📋 Review Request Details
                      </h2>
                      <button
                        onClick={() => setShowDetailModal(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ffffff',
                          fontSize: '1.5rem',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          borderRadius: '4px',
                          lineHeight: 1
                        }}
                      >
                        ✕
                      </button>
                    </div>

                    {/* Modal Content */}
                    <div style={{ padding: '2rem' }}>
                      {/* Student & Request Info */}
                      <div style={{
                        background: '#f8f9fa',
                        padding: '1.5rem',
                        borderRadius: '8px',
                        marginBottom: '1.5rem',
                        border: '1px solid #e9ecef'
                      }}>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                          gap: '1.5rem'
                        }}>
                          <div>
                            <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Student Information</h3>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Name:</strong> {selectedRequest.student_name}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Student ID:</strong> {selectedRequest.student_id}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Status:</strong>
                              <span style={{
                                marginLeft: '0.5rem',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                backgroundColor: getStatusInfo(selectedRequest.status).bg,
                                color: getStatusInfo(selectedRequest.status).color
                              }}>
                                {getStatusInfo(selectedRequest.status).icon} {getStatusInfo(selectedRequest.status).text}
                              </span>
                            </div>
                          </div>

                          <div>
                            <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Request Details</h3>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Semester:</strong> {selectedRequest.semester}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Academic Year:</strong> {selectedRequest.academic_year}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Subject:</strong> {selectedRequest.subject_code || 'Full Marks Card Review'}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Submitted:</strong> {formatDate(selectedRequest.created_at)} at {formatTime(selectedRequest.created_at)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Reason for Review */}
                      <div style={{
                        background: '#f8f9fa',
                        padding: '1.5rem',
                        borderRadius: '8px',
                        marginBottom: '1.5rem',
                        border: '1px solid #e9ecef'
                      }}>
                        <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Reason for Review</h3>
                        <p style={{ margin: 0, fontSize: '1rem', color: '#333', lineHeight: 1.6 }}>
                          {selectedRequest.reason}
                        </p>
                      </div>

                      {/* Review Section */}
                      {(selectedRequest.status === 'pending' || selectedRequest.status === 'in-review') && (
                        <div style={{
                          background: '#fff3cd',
                          padding: '1.5rem',
                          borderRadius: '8px',
                          marginBottom: '1.5rem',
                          border: '1px solid #ffeaa7'
                        }}>
                          <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Review Action</h3>
                          <textarea
                            value={reviewNotes}
                            onChange={(e) => setReviewNotes(e.target.value)}
                            placeholder="Add review notes (optional)..."
                            rows={3}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '2px solid #e9ecef',
                              borderRadius: '8px',
                              fontSize: '0.95rem',
                              resize: 'vertical',
                              marginBottom: '1rem'
                            }}
                          />
                          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {selectedRequest.status === 'pending' && (
                              <button
                                onClick={() => handleReviewAction(selectedRequest.id, 'in-review', reviewNotes)}
                                disabled={actionLoading}
                                style={{
                                  padding: '0.75rem 1.5rem',
                                  background: '#17a2b8',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontWeight: '500',
                                  opacity: actionLoading ? 0.7 : 1
                                }}
                              >
                                {actionLoading ? '⏳ Processing...' : '🔍 Start Review'}
                              </button>
                            )}

                            <button
                              onClick={() => handleReviewAction(selectedRequest.id, 'completed', reviewNotes, hodData.name || 'HOD')}
                              disabled={actionLoading}
                              style={{
                                padding: '0.75rem 1.5rem',
                                background: '#28a745',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                opacity: actionLoading ? 0.7 : 1
                              }}
                            >
                              {actionLoading ? '⏳ Processing...' : '✅ Complete Review'}
                            </button>

                            <button
                              onClick={() => handleReviewAction(selectedRequest.id, 'rejected', reviewNotes, hodData.name || 'HOD')}
                              disabled={actionLoading}
                              style={{
                                padding: '0.75rem 1.5rem',
                                background: '#dc3545',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                opacity: actionLoading ? 0.7 : 1
                              }}
                            >
                              {actionLoading ? '⏳ Processing...' : '❌ Reject Request'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Existing Review */}
                      {selectedRequest.review_notes && (
                        <div style={{
                          background: selectedRequest.status === 'completed' ? '#d4edda' : '#f8d7da',
                          padding: '1.5rem',
                          borderRadius: '8px',
                          border: `1px solid ${selectedRequest.status === 'completed' ? '#c3e6cb' : '#f5c6cb'}`
                        }}>
                          <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Review History</h3>
                          <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                            Reviewed by <strong>{selectedRequest.reviewed_by}</strong> on {formatDate(selectedRequest.reviewed_at)}
                          </div>
                          <p style={{ margin: 0, fontSize: '1rem', color: '#333' }}>
                            {selectedRequest.review_notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </>
          )}
        </>
      )}
    </Layout>
  );
};

export default HodReviewRequests;